import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { useHideOnClickOutside } from 'hooks/useHideOnClickOutside';
import Loading from './Loading';
import { IMultiSearchDropdownOption, MultiSearchDropdownOptions } from './MultiSearchDropdownOptions';
import { MultiSearchTag } from './MultiSearchTag';

export interface IMultiSearchDropdownProps {
  fieldId: string;
  values: string[];
  text: string;
  options: IMultiSearchDropdownOption[];
  onValueChange: (values: string[]) => void;
  onTextChange: (value: string) => void;
  label?: string;
  maxVisibleItems?: number;
  withTags?: boolean;
  className?: string;
  inputClassName?: string;
  loading?: boolean;
  errorMessage?: string | null;
  errorClassName?: string;
  disabled?: boolean;
  inputPlaceholder?: string;
  hideSelectedFromDropdown?: boolean;
}

export const MultiSearchDropdown: React.FC<IMultiSearchDropdownProps> = React.memo(
  ({
    fieldId,
    text,
    values,
    options,
    withTags = true,
    onTextChange,
    onValueChange,
    label,
    maxVisibleItems,
    className = '',
    inputClassName = '',
    loading = false,
    errorMessage,
    errorClassName,
    disabled = false,
    inputPlaceholder = 'Add label',
    hideSelectedFromDropdown = false,
  }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const inputClassname = classnames('w-full text-15px leading-19px py-7px px-10px font-pt-sans outline-none', {
      'border-gray-40 bg-ivory': !errorMessage && !disabled,
      'border-red-95 bg-red-25': !!errorMessage && !disabled,
      'text-black border border-solid': !disabled,
      'text-gray-100 border-none bg-gray-10': disabled,
    });

    const { instanceClassname } = useHideOnClickOutside(setIsOpen, 'select');

    const openDropdown = useCallback(() => {
      !disabled && setIsOpen(true);
    }, [disabled]);

    const handleInputChange = useCallback(
      e => {
        onTextChange(e.target.value);
      },
      [onTextChange]
    );

    const handleChoose = useCallback(
      (value: string) => {
        const newValues = values.includes(value)
          ? values.filter(item => item !== value)
          : [...values, value];
        onValueChange(newValues);
        setIsOpen(false);
      },
      [values, onValueChange]
    );

    const handleTagDelete = useCallback(
      (value: string) => {
        if (disabled) {
          return;
        }
        const newValues = values.filter(item => item !== value);
        onValueChange(newValues);
      },
      [values, disabled, onValueChange]
    );

    return (
      <div className={classnames(instanceClassname, 'flex flex-col min-w-75px', className)}>
        {label && (
          <label htmlFor={fieldId} className="font-pt-sans mb-5px text-black text-13px leading-17px tracking-2xs">
            {label}
          </label>
        )}
        <div
          id={fieldId}
          className={classnames(
            'relative flex flex-col w-full cursor-pointer items-start justify-start text-15px',
            inputClassName
          )}
        >
          <div className="options flex items-center justify-start relative h-full w-full">
            <input
              autoComplete="off"
              className={inputClassname}
              id={fieldId}
              onChange={handleInputChange}
              value={text}
              placeholder={inputPlaceholder}
              onFocus={openDropdown}
              disabled={disabled}
            />

            <div
              className={classnames(
                'w-full font-pt-sans absolute top-35px -left-1px z-30 border border-solid border-t-0 border-gray-20 bg-ivory',
                { hidden: !isOpen && !loading }
              )}
            >
              {loading ? (
                <div className="py-2">
                  <Loading />
                </div>
              ) : (
                <MultiSearchDropdownOptions
                  options={options}
                  maxVisibleItems={maxVisibleItems}
                  text={text}
                  values={values}
                  onChoose={handleChoose}
                  hideSelectedFromDropdown={hideSelectedFromDropdown}
                />
              )}
            </div>
          </div>
          {withTags && (
            <div className="tags flex flex-wrap gap-10px mt-10px">
              {options.filter(option => option.value && values.includes(option.value)).map(option => {
                return (
                  <MultiSearchTag
                    key={option.value}
                    value={option.value}
                    label={option.label ?? ''}
                    onDelete={handleTagDelete}
                  />
              )})}
            </div>
          )}
        </div>

        {errorMessage && (
          <p className={`text-red-95 text-13px leading-2xs font-pt-sans mt-5px ${errorClassName ?? ''}`}>
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);
